import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";
import { API_ROUTES } from "../config/api";
import { LifeCycleChain } from "../types/api.types";

export const useLifeCycles = (dbId: string, articleIds: string[]) => {
    const { data, isLoading, error } = useQuery({
        queryKey: [API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.GET_LIFE_CYCLES(dbId), articleIds],
        queryFn: () => api.post(API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.GET_LIFE_CYCLES(dbId), {
            article_ids: articleIds
        }),
        enabled: !!dbId && articleIds.length > 0
    });
    
    return {
        lifeCycles: data?.data || [],
        isLoading,
        error
    };
};

export const useLifeCycle = (dbId: string, articleId: string) => {
    const { data, isLoading, error } = useQuery({
        queryKey: [API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.GET_LIFE_CYCLE(dbId, articleId)],
        queryFn: () => api.get(API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.GET_LIFE_CYCLE(dbId, articleId)),
        enabled: !!dbId && !!articleId
    });
    
    return {
        lifeCycle: data?.data,
        isLoading,
        error
    };
};  

export const useSetLifeCycle = (dbId: string, articleId: string) => {
    const { mutate } = useMutation({
        mutationFn: (lifeCycle: LifeCycleChain) => api.post(API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.SET_LIFE_CYCLE(dbId, articleId), lifeCycle),
    });
    return mutate;
};

export const useSetLifeCycleStatus = (dbId: string, articleId: string) => {
    const queryClient = useQueryClient();
    
    const { mutate, isPending, error } = useMutation({
        mutationFn: (data: { status: string; date: string }) => 
            api.post(API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.SET_LIFE_CYCLE(dbId, articleId), data),
        onSuccess: () => {
            // Invalidate and refetch lifecycle data
            queryClient.invalidateQueries({
                queryKey: [API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.GET_LIFE_CYCLES(dbId)]
            });
            queryClient.invalidateQueries({
                queryKey: [API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.GET_LIFE_CYCLE(dbId, articleId)]
            });
        }
    });
    
    return {
        setLifeCycleStatus: mutate,
        isLoading: isPending,
        error
    };
};

export const useLifeCycleRecommendations = (dbId: string, articleIds: string[]) => {
    const { data, isLoading, error } = useQuery({
        queryKey: [API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.RECOMMENDATIONS(dbId), articleIds],
        queryFn: () => api.post(
            API_ROUTES.LIFE_CYCLE.BASE + API_ROUTES.LIFE_CYCLE.RECOMMENDATIONS(dbId),
            { article_ids: articleIds }
        ),
        enabled: !!dbId && articleIds.length > 0
    });

    return {
        recommendations: (data?.data || {}) as Record<string, string>,
        isLoading,
        error
    };
};